import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { useFormasPagamento } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { FormasPagamentoTable } from '@/features/financeiro/components/FormasPagamentoTable';
import { FormaPagamentoDialog } from '@/features/financeiro/components/FormaPagamentoDialog';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import type { FormaPagamento, SalvarFormaPagamentoRequest } from '@/features/financeiro/types';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const FormasPagamento = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingForma, setEditingForma] = useState<FormaPagamento | null>(null);
  const [busca, setBusca] = useState('');

  const { response, isLoading, criar, atualizar, inativar, isCriando, isAtualizando } = useFormasPagamento(busca);
  const formas = response?.itens ?? [];
  const paginacao = usePaginacaoLocal(formas);

  const handleOpenDialog = async (forma?: FormaPagamento) => {
    if (forma?.id) {
      const detalhe = await cadastrosFinanceirosService.getFormaPagamentoById(forma.id);
      setEditingForma(detalhe);
    } else {
      setEditingForma(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingForma(null);
  };

  const handleSubmit = (data: SalvarFormaPagamentoRequest) => {
    if (editingForma) {
      atualizar({ id: editingForma.id, req: data }, { onSuccess: handleCloseDialog });
    } else {
      criar(data, { onSuccess: handleCloseDialog });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Formas de Pagamento</h1>
          <p className="text-muted-foreground">Gerencie as formas de pagamento e recebimento</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />Nova Forma
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar forma de pagamento..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-10"
        />
      </div>

      <FormasPagamentoTable
        formas={paginacao.itensPaginados}
        onEdit={handleOpenDialog}
        onInativar={(id) => inativar(id)}
      />

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <FormaPagamentoDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingForma={editingForma}
        isLoading={isCriando || isAtualizando}
      />
    </div>
  );
};

export default FormasPagamento;
